/**
 * ============================================================
 * 工资提报页面 (WageDeclaration)
 * ============================================================
 * 功能说明：
 *   - 工资提报列表（只显示已付款账单）
 *   - 统计卡片（未申报/已申报）
 *   - 申报工资按钮（管理员和财务角色可见）
 *   - 取消申报按钮
 * ============================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import { billApi, brokerApi, userApi } from '../api';
import { useAuth } from '../auth';
import type { Bill, Broker, BillStats, User } from '../types';
import {
  Receipt, RefreshCw, CheckCircle, DollarSign, FileText, CalendarDays, Users, AlertCircle,
  Plus, X, User as UserIcon, Phone, CreditCard, Building, Download
} from 'lucide-react';

export default function WageDeclaration() {
  const { user, isAdmin, isFinance } = useAuth();

  // ========== 状态管理 ==========
  const [bills, setBills] = useState<Bill[]>([]);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [stats, setStats] = useState({ total: 0, undeclared: 0, declared: 0 });
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // 新增提报单弹窗状态
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<Broker | null>(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [brokerUserInfo, setBrokerUserInfo] = useState<User | null>(null);
  
  // 可用月份列表（从后端获取）
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);

  // ========== 筛选条件 ==========
  // 管理员和财务角色可以查看所有经纪人数据，普通用户只能查看绑定的经纪人
  const [filter, setFilter] = useState({
    brokerId: '',
    month: '',
    declared: '',
    status: ''  // 默认显示已确认金额和已打款两种状态
  });

  // 用户信息加载完成后更新筛选条件
  useEffect(() => {
    if (user) {
      const newBrokerId = (isAdmin || isFinance) ? '' : ((user.bound_broker_id ? String(user.bound_broker_id) : '') || '');
      setFilter(prev => ({ ...prev, brokerId: newBrokerId }));
    }
  }, [user, isAdmin, isFinance]);

  // ========== Toast ==========
  const showSuccess = useCallback((message: string) => { setToast({ message, type: 'success' }); setTimeout(() => setToast(null), 3000); }, []);
  const showError = useCallback((message: string) => { setToast({ message, type: 'error' }); setTimeout(() => setToast(null), 3000); }, []);

  // ========== 生命周期 ==========
  useEffect(() => { loadInit(); }, []);

  useEffect(() => {
    console.log('[WageDeclaration] filter变化:', filter);
    if (!loading) {
      console.log('[WageDeclaration] 调用loadAllData');
      loadAllData();
    } else {
      console.log('[WageDeclaration] loading为true，跳过loadAllData');
    }
  }, [filter]);

  const loadInit = async () => {
    try {
      const [b, monthsResult] = await Promise.all([
        brokerApi.list().catch(() => []),
        billApi.getAvailableMonths().catch(() => ({ months: [] })),
      ]);
      setBrokers(b);
      
      // 设置可用月份列表
      const months = monthsResult.months || [];
      setAvailableMonths(months);
      
      // 默认选择上月（如果存在）
      if (months.length > 0) {
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        const lastMonthStr = lastMonth.toISOString().slice(0, 7);
        const defaultMonth = months.includes(lastMonthStr) ? lastMonthStr : months[0];
        setFilter(prev => ({ ...prev, month: defaultMonth }));
      }
      
      await loadBills();
      await loadStats();
    } catch (error) {
      console.error('初始化失败:', error);
    } finally { setLoading(false); }
  };

  /** 加载统计数据 */
  const loadStats = async () => {
    try {
      const params: Record<string, unknown> = { personalOnly: true };
      // 根据筛选条件设置参数（不包含 declared 筛选，因为统计需要全部数据）
      if (filter.status) params.status = filter.status;
      if (filter.brokerId) params.brokerId = Number(filter.brokerId);
      if (filter.month) params.month = filter.month;
      
      const statsResult = await billApi.stats(params);
      // 使用数量统计字段
      setStats({ 
        total: statsResult.total || 0, 
        undeclared: (statsResult.wageUndeclared || 0), 
        declared: (statsResult.wageDeclared || 0) 
      });
    } catch (error) {
      console.error('加载统计数据失败:', error);
    }
  };

  /** 加载账单列表 */
  const loadBills = async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { personalOnly: true };
      // 根据筛选条件设置状态
      if (filter.status) params.status = filter.status;
      if (filter.brokerId) params.brokerId = Number(filter.brokerId);
      if (filter.month) params.month = filter.month;
      console.log('[WageDeclaration] filter.declared:', filter.declared, '类型:', typeof filter.declared);
      if (filter.declared === '0') {
        params.wageDeclared = false;
        console.log('[WageDeclaration] 设置 wageDeclared = false');
      }
      if (filter.declared === '1') {
        params.wageDeclared = true;
        console.log('[WageDeclaration] 设置 wageDeclared = true');
      }

      console.log('[WageDeclaration] 加载账单参数:', JSON.stringify(params));
      console.log('[WageDeclaration] wageDeclared参数:', params.wageDeclared, '类型:', typeof params.wageDeclared);
      console.log('[WageDeclaration] 当前用户:', user);
      console.log('[WageDeclaration] isAdmin:', isAdmin, 'isFinance:', isFinance);

      const res = await billApi.list(params);
      console.log('[WageDeclaration] 后端返回:', res);
      console.log('[WageDeclaration] res.data:', res?.data);
      console.log('[WageDeclaration] res.data 类型:', typeof res?.data);
      console.log('[WageDeclaration] res.data 长度:', res?.data?.length);
      
      const billData = res.data || [];
      console.log('[WageDeclaration] billData:', billData);
      console.log('[WageDeclaration] billData 长度:', billData.length);

      setBills(billData);
    } catch (error) {
      console.error('加载账单列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  /** 同时加载统计和账单数据 */
  const loadAllData = async () => {
    await loadStats();
    await loadBills();
  };

  /** 申报工资 */
  const handleDeclare = async (billId: number) => {
    setProcessingId(billId);
    try {
      await billApi.wageDeclare(billId);
      showSuccess('申报成功');
      await loadBills();
    } catch (err: any) {
      showError(err.message || '申报失败');
    } finally {
      setProcessingId(null);
    }
  };

  /** 取消申报 */
  const handleUndeclare = async (billId: number) => {
    setProcessingId(billId);
    try {
      await billApi.wageUndeclare(billId);
      showSuccess('取消申报成功');
      await loadBills();
    } catch (err: any) {
      showError(err.message || '取消申报失败');
    } finally {
      setProcessingId(null);
    }
  };

  /** 删除提报单（仅管理员可用） */
  const handleDeleteBill = async (billId: number) => {
    if (!confirm('确定要删除这条提报单吗？此操作不可恢复。')) {
      return;
    }
    
    setProcessingId(billId);
    try {
      await billApi.delete(billId);
      showSuccess('删除成功');
      await loadBills();
    } catch (err: any) {
      showError(err.message || '删除失败');
    } finally {
      setProcessingId(null);
    }
  };

  // ========== 新增提报单功能 ==========
  /** 打开新增提报单弹窗 */
  const openAddModal = () => {
    setSelectedBroker(null);
    // 默认选中上月（如果存在）
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    const lastMonthStr = lastMonth.toISOString().slice(0, 7);
    const defaultMonth = availableMonths.includes(lastMonthStr) ? lastMonthStr : availableMonths[0] || '';
    setSelectedMonth(defaultMonth);
    setManualAmount('');
    setBrokerUserInfo(null);
    setShowAddModal(true);
  };

  /** 关闭新增提报单弹窗 */
  const closeAddModal = () => {
    setShowAddModal(false);
    setSelectedBroker(null);
    setSelectedMonth('');
    setManualAmount('');
    setBrokerUserInfo(null);
  };

  /** 处理经纪人选择变化 */
  const handleBrokerChange = async (brokerId: string) => {
    const broker = brokers.find(b => b.id === Number(brokerId));
    console.log('[WageDeclaration] 选择经纪人:', { 
      brokerId, 
      broker,
      brokerName: broker?.name,
      boundUserId: broker?.boundUserId,
      hasBoundUserId: broker?.boundUserId !== undefined && broker?.boundUserId !== null && broker?.boundUserId > 0
    });
    
    setSelectedBroker(broker || null);
    setBrokerUserInfo(null);
    
    if (broker && broker.boundUserId) {
      console.log('[WageDeclaration] 开始查询绑定用户，用户ID:', broker.boundUserId);
      try {
        const userInfo = await userApi.getById(broker.boundUserId);
        console.log('[WageDeclaration] 获取绑定用户信息成功:', {
          userId: userInfo?.id,
          name: userInfo?.name,
          name_real: userInfo?.name_real,
          phone: userInfo?.phone,
          id_card: userInfo?.id_card ? '已设置' : '未设置',
          bank_card: userInfo?.bank_card ? '已设置' : '未设置',
          userInfo: userInfo
        });
        setBrokerUserInfo(userInfo);
      } catch (e: any) {
        console.error('[WageDeclaration] 获取绑定用户信息失败:', { error: e.message, stack: e.stack });
      }
    } else {
      console.log('[WageDeclaration] 经纪人未绑定用户:', { brokerName: broker?.name, boundUserId: broker?.boundUserId });
    }
  };

  /** 提交新增提报单 */
  const handleSubmitManualBill = async () => {
    if (!selectedBroker || !selectedMonth || !manualAmount) {
      showError('请填写完整信息');
      return;
    }
    
    const amount = parseFloat(manualAmount);
    if (isNaN(amount) || amount <= 0) {
      showError('请输入有效的金额');
      return;
    }
    
    setProcessingId(-1); // 使用-1表示正在处理新增操作
    
    try {
      await billApi.createManual({
        brokerId: selectedBroker.id,
        settlementMonth: selectedMonth,
        shareAmount: amount
      });
      
      showSuccess('新增提报单成功');
      closeAddModal();
      await loadBills();
    } catch (err: any) {
      showError(err.message || '新增提报单失败');
    } finally {
      setProcessingId(null);
    }
  };

  /** 导出Excel */
  const handleExportExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      
      const exportData = bills.map(bill => ({
        '月份': bill.settlementMonth || '',
        '姓名': (bill as any).brokerRealName || bill.brokerName || '',
        '身份证号': (bill as any).brokerIdCard || '',
        '手机号': (bill as any).brokerPhone || '',
        '金额': bill.shareAmount || 0
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '工资提报');
      
      const fileName = `工资提报_${filter.month || '全部月份'}_${new Date().toLocaleDateString('zh-CN')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      showSuccess('导出成功');
    } catch (error) {
      console.error('导出Excel失败:', error);
      showError('导出失败');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#3B82F6]" />
          工资提报
        </h1>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <button onClick={openAddModal}
              className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] hover:bg-[#2563EB] rounded-lg text-sm transition-all">
              <Plus className="w-4 h-4" /> 新增提报单
            </button>
          )}
          <button onClick={handleExportExcel}
            className="flex items-center gap-2 px-4 py-2 bg-[#10B981] hover:bg-[#059669] rounded-lg text-sm transition-all">
            <Download className="w-4 h-4" /> 导出Excel
          </button>
          <button onClick={loadBills}
            className="flex items-center gap-2 px-4 py-2 bg-[#334155] hover:bg-[#475569] rounded-lg text-sm transition-all">
            <RefreshCw className="w-4 h-4" /> 刷新
          </button>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {[
            { label: '总提报单', value: stats.total, color: 'text-blue-400', icon: Receipt, declared: '' },
            { label: '未申报', value: stats.undeclared, color: 'text-red-400', icon: AlertCircle, declared: '0' },
            { label: '已申报', value: stats.declared, color: 'text-emerald-400', icon: CheckCircle, declared: '1' },
          ].map((s, i) => (
            <div
              key={i}
              className={`bg-[#1E293B] border rounded-xl p-4 cursor-pointer transition-all hover:border-[#3B82F6] hover:bg-[#0F172A] ${
                filter.declared === s.declared ? 'border-[#3B82F6] bg-[#0F172A]' : 'border-[#334155]'
              }`}
              onClick={() => setFilter({ ...filter, declared: s.declared })}
            >
              <div className="flex items-center gap-2">
                <s.icon className={`w-5 h-5 ${s.color}`} />
                <div>
                  <p className="text-xs text-[#94A3B8]">{s.label}</p>
                  <p className={`text-xl font-bold ${s.color}`}>{typeof s.value === 'number' ? s.value.toLocaleString('zh-CN') : s.value}</p>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* 筛选栏 */}
      <div className="flex flex-wrap gap-3 bg-[#1E293B] border border-[#334155] rounded-xl p-4">
        {isAdmin || isFinance ? (
          <select value={filter.brokerId} onChange={e => setFilter({ ...filter, brokerId: e.target.value })}
            className="h-9 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
            <option value="">全部经纪人</option>
            {brokers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        ) : (
          <div className="h-9 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm flex items-center">
            <span className="text-[#F1F5F9]">{user?.bound_broker_name || '未绑定经纪人'}</span>
          </div>
        )}

        {/* 月份筛选 */}
        <select value={filter.month} onChange={e => setFilter({ ...filter, month: e.target.value })}
          className="h-9 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
          <option value="">全部月份</option>
          {availableMonths.map(month => (
            <option key={month} value={month}>{month}</option>
          ))}
        </select>

        {/* 状态筛选：已确认金额 / 已打款 */}
        <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}
          className="h-9 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
          <option value="">全部</option>
          <option value="confirmed">已确认金额</option>
          <option value="paid">已打款</option>
        </select>

        <select value={filter.declared} onChange={e => setFilter({ ...filter, declared: e.target.value })}
          className="h-9 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
          <option value="">全部</option>
          <option value="0">未申报</option>
          <option value="1">已申报</option>
        </select>
      </div>

      {/* 表格 */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
        ) : bills.length === 0 ? (
          <div className="text-center py-16 text-[#94A3B8]">暂无账单</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#334155] bg-[#0F172A]">
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">月份</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">姓名</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">身份证</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">手机号</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">银行卡</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">开户行</th>
                  <th className="px-4 py-3 text-right text-xs text-[#F59E0B] font-medium">金额</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">状态</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">申报人</th>
                  <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">申报日期</th>
                  {(isAdmin || isFinance) && (
                    <th className="px-4 py-3 text-right text-xs text-[#94A3B8] font-medium">操作</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#334155]/50">
                {bills.map(bill => {
                  const isDeclared = bill.wageDeclared === true || bill.wageDeclared === "1";
                  return (
                    <tr key={bill.id} className="hover:bg-[#0F172A]/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">{bill.settlementMonth}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{bill.brokerRealName || '-'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#94A3B8] font-mono">{bill.brokerIdCard || '-'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#94A3B8]">{bill.brokerPhone || '-'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#94A3B8] font-mono">{bill.brokerBankCard || '-'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#94A3B8]">{bill.brokerBankName || '-'}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <span className="font-bold text-[#F59E0B]">
                          {bill.shareAmount != null && !isNaN(Number(bill.shareAmount)) 
                            ? `¥${Number(bill.shareAmount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                            : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isDeclared ? (
                          <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded">已申报</span>
                        ) : (
                          <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">未申报</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#94A3B8]">
                        {bill.wageDeclaredBy || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#94A3B8]">
                        {bill.wageDeclaredAt ? new Date(bill.wageDeclaredAt).toLocaleString('zh-CN', {
                          year: 'numeric', month: '2-digit', day: '2-digit',
                          hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Shanghai'
                        }) : '-'}
                      </td>
                      {(isAdmin || isFinance) && (
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isDeclared ? (
                              <button
                                onClick={() => handleUndeclare(bill.id)}
                                disabled={processingId === bill.id}
                                className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs rounded transition-colors disabled:opacity-50"
                              >
                                {processingId === bill.id ? '处理中...' : '取消申报'}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleDeclare(bill.id)}
                                disabled={processingId === bill.id}
                                className="px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs rounded transition-colors disabled:opacity-50"
                              >
                                {processingId === bill.id ? '处理中...' : '申报工资'}
                              </button>
                            )}
                            {isAdmin && bill.isManual && (
                              <button
                                onClick={() => handleDeleteBill(bill.id)}
                                disabled={processingId === bill.id}
                                className="px-3 py-1.5 bg-gray-500/10 hover:bg-gray-500/20 text-gray-400 text-xs rounded transition-colors disabled:opacity-50"
                              >
                                {processingId === bill.id ? '处理中...' : '删除'}
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg z-50 animate-fade-in ${
          toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      {/* 新增提报单弹窗 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl w-full max-w-lg shadow-xl">
            {/* 弹窗头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#334155]">
              <h2 className="text-lg font-semibold text-white">新增提报单</h2>
              <button onClick={closeAddModal} className="text-[#94A3B8] hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* 弹窗内容 */}
            <div className="p-6 space-y-4">
              {/* 选择经纪人 */}
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">选择经纪人 *</label>
                <select 
                  value={selectedBroker?.id || ''}
                  onChange={(e) => handleBrokerChange(e.target.value)}
                  className="w-full h-10 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm focus:border-[#3B82F6] focus:outline-none text-white"
                >
                  <option value="">请选择经纪人</option>
                  {brokers.map(b => (
                    <option key={b.id} value={b.id}>{b.name} {b.boundUserName ? `(${b.boundUserName})` : ''}</option>
                  ))}
                </select>
              </div>
              
              {/* 选择月份 */}
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">提报月份 *</label>
                <select 
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full h-10 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm focus:border-[#3B82F6] focus:outline-none text-white"
                >
                  <option value="">请选择月份</option>
                  {availableMonths.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>
              
              {/* 输入金额 */}
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">提报金额 (元) *</label>
                <input 
                  type="number"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="请输入金额"
                  className="w-full h-10 px-3 bg-[#0F172A] border border-[#334155] rounded-lg text-sm focus:border-[#3B82F6] focus:outline-none text-white placeholder-[#64748B]"
                  step="0.01"
                  min="0"
                />
              </div>
              
              {/* 经纪人信息预览 */}
              {selectedBroker && (
                <div className="bg-[#0F172A] rounded-lg p-4">
                  <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <UserIcon className="w-4 h-4" /> 经纪人信息
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-[#94A3B8]">
                      <UserIcon className="w-4 h-4" />
                      <span>姓名: {brokerUserInfo?.name_real || brokerUserInfo?.name || selectedBroker.boundUserName || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#94A3B8]">
                      <Phone className="w-4 h-4" />
                      <span>手机号: {brokerUserInfo?.phone || '未绑定'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#94A3B8]">
                      <CreditCard className="w-4 h-4" />
                      <span>身份证: {brokerUserInfo?.id_card ? `${brokerUserInfo.id_card.slice(0, 4)}********${brokerUserInfo.id_card.slice(-4)}` : '未绑定'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#94A3B8]">
                      <Building className="w-4 h-4" />
                      <span>银行卡: {brokerUserInfo?.bank_card ? `${brokerUserInfo.bank_card.slice(-4)}` : '未绑定'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* 弹窗底部 */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#334155]">
              <button 
                onClick={closeAddModal}
                className="px-4 py-2 bg-[#334155] hover:bg-[#475569] rounded-lg text-sm transition-colors"
              >
                取消
              </button>
              <button 
                onClick={handleSubmitManualBill}
                disabled={processingId !== null}
                className="px-4 py-2 bg-[#3B82F6] hover:bg-[#2563EB] disabled:bg-[#64748B] rounded-lg text-sm transition-colors"
              >
                {processingId !== null ? '提交中...' : '提交'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
